import { useCallback, useEffect, useMemo, useRef } from 'react';

import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';
import { createNativeEmbedChildClient } from '@atlassian/native-embeds-core/services/client/child/main';
import { maybeParseNativeEmbedParams } from '@atlassian/native-embeds-core/services/client/iframe-integration/main';
import {
	NativeEmbedCoreFeature,
	type NativeEmbedFeature,
} from '@atlassian/native-embeds-core/types/features/main';
import type { NativeEmbedChildCommand } from '@atlassian/native-embeds-core/types/commands/types';

import { UNSAFE_expValNoExposure } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

export type ReadPageContent = {
	contentId: string;
	/**
	 * @returns the stringified page ADF.
	 */
	getPageAdf: () => string | Promise<string>;
};

/**
 * Initialises the native embed child client when a page is running inside a
 * native embed iframe (i.e. the URL contains `nativeEmbedChildId` and
 * `nativeEmbedParentId` query params set by the host). Gated behind the
 * `cc-maui-experiment` experiment.
 *
 * Should be called from embedded view pages, embedded edit pages, and
 * embedded live docs.
 *
 * @returns sendChildCommand — a stable callback to send a child command to the
 * native embed host, or a no-op if not running inside a native embed iframe.
 * @returns setReadPageContent — a stable callback to register a page-content provider
 * for read-content commands, or a no-op if not running inside a native embed iframe.
 */
export const usePageNativeEmbedClient = (): {
	sendChildCommand: (command: NativeEmbedChildCommand) => void;
	triggerToggleFullScreen: () => void;
	setReadPageContent: (readPageContent: ReadPageContent) => void;
	isNativeEmbed: boolean;
} => {
	const clientRef = useRef<ReturnType<typeof createNativeEmbedChildClient> | null>(null);

	/**
	 * Use a ref to store the read page content provider. This is necessary to avoid the case
	 * where the read page content provider is set before the client is created, causing the client
	 * to not receive the read page content provider.
	 */
	const readPageContentRef = useRef<ReadPageContent | null>(null);
	const registerReadPageContent = useCallback(
		(client: ReturnType<typeof createNativeEmbedChildClient>, readPageContent: ReadPageContent) => {
			client.onReadContent(async () => {
				const { contentId, getPageAdf } = readPageContent;
				const defaultResult = {
					contentType: 'embeddedPage' as const,
					embeddedPageBody: '',
					contentId,
				};

				try {
					const embeddedPageBody = await getPageAdf();

					return {
						...defaultResult,
						embeddedPageBody,
					};
				} catch {
					return defaultResult;
				}
			});
		},
		[],
	);

	const nativeEmbedParams = useMemo(() => {
		const isNativeEmbedEnabled =
			UNSAFE_expValNoExposure('cc-maui-experiment', 'isEnabled', false) ||
			fg('platform_native_embeds_rollout_non_maui_experience');

		if (!isNativeEmbedEnabled || typeof window === 'undefined') {
			return null;
		}

		return maybeParseNativeEmbedParams(new URLSearchParams(window.location.search));
	}, []);
	const isNativeEmbed = !!nativeEmbedParams;

	useEffect(() => {
		if (!nativeEmbedParams) {
			return;
		}

		const bridge = getBrowserBridgeAPI();
		const { childClientId, parentClientId } = nativeEmbedParams;

		const supportedFeatures = new Set<NativeEmbedFeature>([NativeEmbedCoreFeature.NotifyHostClick]);

		const client = createNativeEmbedChildClient(
			bridge,
			childClientId,
			parentClientId,
			supportedFeatures,
		);

		clientRef.current = client;
		if (readPageContentRef.current && fg('maui_remixable_page_native_embeds')) {
			registerReadPageContent(client, readPageContentRef.current);
		}

		return () => {
			client.destroy();
			clientRef.current = null;
		};
	}, [nativeEmbedParams, registerReadPageContent]);

	const sendChildCommand = useCallback((command: NativeEmbedChildCommand) => {
		clientRef.current?.sendChildCommand(command);
	}, []);

	const triggerToggleFullScreen = useCallback(() => {
		clientRef.current?.sendToggleFullScreen();
	}, []);

	const setReadPageContent = useCallback(
		(readPageContent: ReadPageContent) => {
			readPageContentRef.current = readPageContent;
			if (clientRef.current && fg('maui_remixable_page_native_embeds')) {
				registerReadPageContent(clientRef.current, readPageContent);
			}
		},
		[registerReadPageContent],
	);

	return { sendChildCommand, triggerToggleFullScreen, setReadPageContent, isNativeEmbed };
};
